import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.vfs;

// ---- Design tokens ---------------------------------------------------------
export const COLORS = {
    primary: "#8B2500",       // deep temple maroon
    primaryLight: "#FBF1EC",
    zebra: "#FAF7F5",
    border: "#E3D9D3",
    text: "#2B2B2B",
    muted: "#7A6F6A",
};

// SN | Receipt No | Receipt Date | Devotee | Mobile | Seva Name | Seva Date | Last col
export const COLUMN_WIDTHS = [22, 50, 56, "*", 62, "*", 56, 60];
const COLS = COLUMN_WIDTHS.length;

// ---- Formatters ------------------------------------------------------------
export const fmtLongDate = (d) =>
    d
        ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
        : "";

export const fmtAmount = (n) =>
    (parseFloat(n) || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const fmtINR = (n) => `₹${fmtAmount(n)}`;

export const sumBy = (list, key) => list.reduce((s, i) => s + (parseFloat(i[key]) || 0), 0);

// ---- Table building blocks -------------------------------------------------
const th = (text, alignment = "left") => ({
    text,
    alignment,
    bold: true,
    fontSize: 9,
    color: "#FFFFFF",
    margin: [0, 3, 0, 3],
});

/** Header row. lastColLabel e.g. "Amount (₹)" or "Persons" */
export const headerRow = (lastColLabel) => [
    th("SN", "center"),
    th("Receipt No"),
    th("Receipt Date"),
    th("Devotee"),
    th("Mobile"),
    th("Seva Name"),
    th("Seva Date"),
    th(lastColLabel, "right"),
];

/** One data row. `lastCol` is the already-formatted text for the last column. */
export const dataRow = (item, index, lastCol, formatDate) => {
    const fillColor = index % 2 === 1 ? COLORS.zebra : undefined;
    return [
        { text: index + 1, alignment: "center", color: COLORS.muted, fillColor },
        { text: item.receiptNo || "-", bold: true, fillColor },
        { text: formatDate(item.receiptDate) || "-", fillColor },
        { text: item.devoteeName || "-", fillColor },
        { text: item.mobile || "-", fillColor },
        { text: item.poojaName || "-", fillColor },
        { text: formatDate(item.poojaDateTime1) || "-", fillColor },
        { text: lastCol, alignment: "right", fillColor },
    ];
};

/** Full-width section band, e.g. "Cash Collection". */
export const bandRow = (text) => [
    {
        text,
        colSpan: COLS,
        alignment: "left",
        bold: true,
        fontSize: 10,
        color: COLORS.primary,
        fillColor: COLORS.primaryLight,
        margin: [0, 2, 0, 2],
    },
    ...Array(COLS - 1).fill({}),
];

/**
 * Sub-total / grand-total row.
 * `extra` (optional) fills the second-to-last column, e.g. a persons total.
 */
export const totalRow = (label, value, { grand = false, cols = COLS, extra } = {}) => {
    const style = {
        bold: true,
        fontSize: grand ? 10 : 9,
        color: COLORS.primary,
        fillColor: grand ? COLORS.primaryLight : undefined,
    };
    const hasExtra = extra !== undefined;
    const labelSpan = cols - (hasExtra ? 2 : 1);
    return [
        { text: label, colSpan: labelSpan, alignment: "right", ...style },
        ...Array(labelSpan - 1).fill({}),
        ...(hasExtra ? [{ text: String(extra), alignment: "right", ...style }] : []),
        { text: value, alignment: "right", ...style },
    ];
};

export const emptyRow = (message = "No records found", cols = COLS) => [
    { text: message, colSpan: cols, alignment: "center", color: COLORS.muted, margin: [0, 8, 0, 8] },
    ...Array(cols - 1).fill({}),
];

/** Generic header row: headerRowOf([["SN","center"], ["Name"], ["Amount","right"]]) */
export const headerRowOf = (labels) => labels.map(([text, align]) => th(text, align));

/** Generic data row with zebra striping: cells are plain pdfmake cell objects. */
export const zebraRow = (cells, index) =>
    cells.map((c) => ({ ...c, fillColor: index % 2 === 1 ? COLORS.zebra : undefined }));

// ---- Document --------------------------------------------------------------
/**
 * @param {string}  logoBase64
 * @param {string}  title      banner title, e.g. "Daily Seva List"
 * @param {Array}   summary    [{ label, value, accent? }] — up to 3 items
 * @param {Array}   body       table body (header row first)
 * @param {Array}   widths     optional column widths (default: 8-column layout)
 * @param {number}  cellPadding optional left/right cell padding (default 5)
 */
export const buildDocDefinition = ({ logoBase64, title, summary, body, widths = COLUMN_WIDTHS, cellPadding = 5 }) => {
    const aligns = ["left", "center", "right"];

    return {
        pageSize: "A4",
        pageMargins: [30, 30, 30, 45],
        defaultStyle: { fontSize: 9, color: COLORS.text, lineHeight: 1.15 },

        content: [
            logoBase64
                ? { image: "templeLogo", width: 535, alignment: "center", margin: [0, 0, 0, 12] }
                : {},

            // Title banner
            {
                table: {
                    widths: ["*"],
                    body: [[{
                        text: title.toUpperCase(),
                        alignment: "center",
                        bold: true,
                        fontSize: 15,
                        color: COLORS.primary,
                        characterSpacing: 1.5,
                        margin: [0, 7, 0, 7],
                    }]],
                },
                layout: {
                    fillColor: () => COLORS.primaryLight,
                    hLineWidth: (i) => (i === 0 || i === 1 ? 1.5 : 0),
                    hLineColor: () => COLORS.primary,
                    vLineWidth: () => 0,
                },
                margin: [0, 0, 0, 10],
            },

            // Summary strip
            {
                columns: summary.map((s, i) => ({
                    width: i === 1 ? "*" : "auto",
                    alignment: aligns[i] || "left",
                    text: [
                        { text: `${s.label}  `, color: COLORS.muted },
                        { text: String(s.value), bold: true, color: s.accent ? COLORS.primary : COLORS.text },
                    ],
                })),
                fontSize: 10,
                margin: [0, 0, 0, 10],
            },

            // Main table
            {
                table: {
                    headerRows: 1,
                    dontBreakRows: true,
                    widths,
                    body,
                },
                layout: {
                    fillColor: (row) => (row === 0 ? COLORS.primary : null),
                    hLineWidth: (i) => (i <= 1 ? 1 : 0.4),
                    hLineColor: (i) => (i <= 1 ? COLORS.primary : COLORS.border),
                    vLineWidth: () => 0,
                    paddingLeft: () => cellPadding,
                    paddingRight: () => cellPadding,
                    paddingTop: () => 4,
                    paddingBottom: () => 4,
                },
            },
        ],

        footer: (currentPage, pageCount) => ({
            margin: [30, 12, 30, 0],
            columns: [
                {
                    text: `Generated on ${new Date().toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}`,
                    fontSize: 8,
                    color: COLORS.muted,
                },
                { text: `Page ${currentPage} of ${pageCount}`, alignment: "right", fontSize: 8, color: COLORS.muted },
            ],
        }),

        images: logoBase64 ? { templeLogo: logoBase64 } : {},
    };
};

export const openPdf = (docDefinition) =>
    new Promise((resolve) => {
        pdfMake.createPdf(docDefinition).open();
        setTimeout(resolve, 500);
    });

/** Payment-type keys → labels (used by collection reports). */
export const PAYMENT_TYPES = {
    CA: "Cash",
    CQ: "Cheque",
    DC: "Credit/Debit Card",
    RT: "RTGS",
    UP: "UPI",
    NB: "Net Banking",
    WL: "Wallet",
    Online: "Online",
};

/** Builds the grouped-by-payment-type body used by both collection reports. */
export const buildCollectionBody = (map, amountKey, formatDate) => {
    const body = [headerRow("Amount (₹)")];
    if (!map || typeof map !== "object") {
        body.push(emptyRow("No collection data available"));
        return { body, grandTotal: 0, count: 0 };
    }

    let grandTotal = 0;
    let count = 0;

    Object.keys(PAYMENT_TYPES).forEach((key) => {
        const list = map[key] || [];
        if (!list.length) return;

        const subTotal = sumBy(list, amountKey);
        grandTotal += subTotal;
        count += list.length;

        body.push(bandRow(`${PAYMENT_TYPES[key]} Collection`));
        list.forEach((item, i) => body.push(dataRow(item, i, fmtAmount(item[amountKey]), formatDate)));
        body.push(totalRow(`Total (${PAYMENT_TYPES[key]})`, fmtINR(subTotal)));
    });

    if (!count) body.push(emptyRow("No collection data available"));
    else body.push(totalRow("Grand Total", fmtINR(grandTotal), { grand: true }));

    return { body, grandTotal, count };
};